import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { orderBy } from 'lodash-es'
import type { JSX } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useTaskActivity } from '#src/pages/task/UseTaskActivity.tsx'
import { groupTaskLabelActivityEntries } from '#src/utils/GroupLabelActivityEntries.tsx'

export function TaskActivity({ task }: { task: StandardTask }): JSX.Element {
  const { data: pastTasks = [] } = useResourceVersions(StandardTask, task.id, {
    validate: (version) => {
      // Order signature tasks were migrated from "signature task" to
      // "standard task", but historical states were not migrated.
      void version.state
    },
  })

  const taskActivity = useTaskActivity(pastTasks)
  const groupedTaskActivity = groupTaskLabelActivityEntries(taskActivity)
  const taskOriginOf = useVersionOriginTask(pastTasks)

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([task.reference])
  )

  const all = orderBy(
    [...groupedTaskActivity, ...comments],
    (a) => {
      if (a instanceof CommentCommunication) {
        return a.sent
      }
      return a.task.lastUpdated
    },
    ['desc']
  )

  return (
    <>
      {all.map((entry) => {
        if (entry instanceof CommentCommunication) {
          return (
            <ActivityComment key={entry.reference.reference} comment={entry} />
          )
        }
        return (
          <ActivityEntry
            key={entry.key ?? entry.task.versionId}
            author={entry.task.lastUpdatedBy}
            time={entry.task.lastUpdated}
            action={entry.action}
            originTask={taskOriginOf(entry.task)}
          />
        )
      })}
    </>
  )
}
